<template>
  <page-header-wrapper>
    <a-card :bordered="false" title="保险订单">
      <div class="table-page-search-wrapper">
        <a-form layout="inline">
          <a-row :gutter="24">
            <a-col :md="6" :sm="24">
              <a-form-item label="保险方案">
                <a-input
                  v-model="queryParams.name"
                  placeholder="请输入保险方案名称"
                  allow-clear
                  @press-enter="handleQuery"
                />
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="支付状态">
                <a-select
                  v-model="queryParams.bondPayStatus"
                  placeholder="请选择支付状态"
                  allow-clear
                  style="width: 160px"
                >
                  <a-select-option :value="1">待支付</a-select-option>
                  <a-select-option :value="2">已支付</a-select-option>
                  <a-select-option :value="3">已取消</a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="投保状态">
                <a-select
                  v-model="queryParams.underwritingStatus"
                  placeholder="请选择投保状态"
                  allow-clear
                  style="width: 180px"
                >
                  <a-select-option v-for="item in underwritingStatusOptions" :key="item.value" :value="item.value">
                    {{ item.label }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="订单号">
                <a-input
                  v-model="queryParams.customerOrderId"
                  placeholder="请输入订单号"
                  allow-clear
                  @press-enter="handleQuery"
                />
              </a-form-item>
            </a-col>
            <a-col :md="24" :sm="24">
              <span class="table-page-search-submitButtons">
                <a-button type="primary" @click="handleQuery">
                  <a-icon type="search" />查询
                </a-button>
                <a-button style="margin-left: 8px" @click="resetQuery">
                  <a-icon type="redo" />重置
                </a-button>
              </span>
            </a-col>
          </a-row>
        </a-form>
      </div>

      <div class="table-operations">
        <a-button type="primary" @click="handleExport" v-hasPermi="['insuranceOrder:insuranceOrder:export']">
          <a-icon type="download" />导出
        </a-button>
        <table-setting
          :style="{ float: 'right' }"
          :table-size.sync="tableSize"
          v-model="columns"
          :refresh-loading="loading"
          :pagination="false"
          @refresh="getList"
        />
      </div>

      <a-table
        :loading="loading"
        :data-source="tableData"
        :columns="columns"
        :size="tableSize"
        row-key="id"
        :pagination="false"
        :bordered="tableBordered"
        :scroll="{ x: 2600 }"
      >
        <template slot="money" slot-scope="text">
          <span v-if="text != null">￥{{ Number(text).toFixed(2) }}</span>
          <span v-else>—</span>
        </template>
        <template slot="status" slot-scope="text">
          <a-tag :color="bondPayStatusColorMap[text] || 'default'">
            {{ bondPayStatusMap[text] || '—' }}
          </a-tag>
        </template>
        <template slot="source" slot-scope="text">
          {{ sourceMap[text] || '—' }}
        </template>
        <template slot="underwritingStatus" slot-scope="text">
          <a-tag :color="underwritingStatusColorMap[text] || 'default'">
            {{ underwritingStatusMap[text] || '—' }}
          </a-tag>
        </template>
        <template slot="submitDeadline" slot-scope="text">
          {{ formatDateTime(text) }}
        </template>
        <template slot="lastSubmitTime" slot-scope="text">
          {{ formatDateTime(text) }}
        </template>
        <template slot="bondPayDate" slot-scope="text">
          {{ formatDateTime(text) }}
        </template>
        <template slot="policyFileName" slot-scope="text, record">
          <span v-if="hasPolicyList(record)">{{ policyProgressText(record) }}</span>
          <a v-else-if="record.policyFileUrl" @click="viewPolicy(record)">{{ text || '查看保单' }}</a>
          <span v-else>—</span>
        </template>
        <template slot="action" slot-scope="text, record">
          <a @click="openDetail(record)" v-hasPermi="['insuranceOrder:insuranceOrder:query']">查看详情</a>
          <a-divider type="vertical" />
          <a
            v-if="canProcess(record)"
            @click="openStatusModal(record)"
            v-hasPermi="['insuranceOrder:insuranceOrder:edit']"
          >
            更新状态
          </a>
          <a-divider v-if="canProcess(record)" type="vertical" />
          <a
            v-if="canProcess(record)"
            @click="openUploadModal(record)"
            v-hasPermi="['insuranceOrder:insuranceOrder:edit']"
          >
            回传保单
          </a>
        </template>
      </a-table>

      <a-pagination
        class="ant-table-pagination"
        show-size-changer
        show-quick-jumper
        :current="queryParams.pageNum"
        :total="total"
        :page-size="queryParams.pageSize"
        :show-total="value => `共 ${value} 条`"
        @showSizeChange="onShowSizeChange"
        @change="changeSize"
      />
    </a-card>

    <a-modal
      title="保险订单详情"
      :visible="detailVisible"
      width="920px"
      :footer="null"
      :body-style="{ maxHeight: '72vh', overflowY: 'auto', paddingTop: '8px' }"
      wrap-class-name="insurance-order-detail-modal"
      @cancel="closeDetail"
    >
      <a-spin :spinning="detailLoading">
        <div class="insurance-detail-body">
          <section class="insurance-detail-section">
            <div class="insurance-detail-section__title">基本信息</div>
            <a-descriptions
              bordered
              size="small"
              :column="2"
              class="insurance-detail-desc"
              :label-style="detailDescLabelStyle"
            >
              <a-descriptions-item label="保险订单ID">{{ detailForm.id || '—' }}</a-descriptions-item>
              <a-descriptions-item label="订单号">{{ detailForm.orderNo || '—' }}</a-descriptions-item>
              <a-descriptions-item label="保险方案">{{ detailForm.insuranceSchemeName || detailForm.name || '—' }}</a-descriptions-item>
              <a-descriptions-item label="投保工种">{{ detailForm.categoryName || '—' }}</a-descriptions-item>
              <a-descriptions-item label="支付状态">{{ bondPayStatusMap[detailForm.bondPayStatus] || '—' }}</a-descriptions-item>
              <a-descriptions-item label="投保状态">{{ underwritingStatusMap[detailForm.underwritingStatus] || '—' }}</a-descriptions-item>
              <a-descriptions-item label="投保人姓名">{{ detailForm.applicantName || '—' }}</a-descriptions-item>
              <a-descriptions-item label="投保人身份证">{{ detailForm.applicantIdCard || '—' }}</a-descriptions-item>
              <a-descriptions-item label="被保人数">{{ detailForm.insuredCount != null ? detailForm.insuredCount : '—' }}</a-descriptions-item>
              <a-descriptions-item label="支付时间">{{ formatDateTime(detailForm.bondPayDate) }}</a-descriptions-item>
              <a-descriptions-item label="最晚提交时间">{{ formatDateTime(detailForm.submitDeadline) }}</a-descriptions-item>
              <a-descriptions-item label="最新提交时间">{{ formatDateTime(detailForm.lastSubmitTime) }}</a-descriptions-item>
              <a-descriptions-item v-if="!hasPolicyList(detailForm)" label="保单上传时间">
                {{ formatDateTime(detailForm.policyUploadTime) }}
              </a-descriptions-item>
            </a-descriptions>
          </section>

          <section class="insurance-detail-section">
            <div class="insurance-detail-section__title">被保人信息</div>
            <div v-if="detailInsuredPersonList.length > 0" class="detail-insured-list">
              <div
                v-for="(row, idx) in detailInsuredPersonList"
                :key="row.tableKey"
                class="detail-insured-item"
              >
                <div class="detail-insured-item__head">被保人 {{ idx + 1 }}</div>
                <div class="detail-insured-item__row">
                  <span class="detail-insured-label">姓名</span>
                  <span class="detail-insured-value">{{ row.name }}</span>
                </div>
                <div class="detail-insured-item__row">
                  <span class="detail-insured-label">身份证</span>
                  <span class="detail-insured-value">{{ row.idCard }}</span>
                </div>
                <div class="detail-insured-item__row">
                  <span class="detail-insured-label">性别</span>
                  <span class="detail-insured-value">{{ formatDetailInsuredSex(row.sex) }}</span>
                </div>
              </div>
            </div>
            <div v-else class="insurance-detail-empty">—</div>
          </section>

          <section class="insurance-detail-section">
            <div class="insurance-detail-section__title">保单信息</div>
            <template v-if="hasPolicyList(detailForm)">
              <div class="policy-summary">{{ policyProgressText(detailForm) }}</div>
              <a-table
                class="policy-table"
                :columns="detailPolicyColumns"
                :data-source="policyListRows(detailForm)"
                :pagination="false"
                size="small"
                row-key="id"
              >
                <template slot="detailPolicyInsured" slot-scope="text, record">
                  <div>{{ record.insuredName || '—' }}</div>
                  <div class="policy-card-no">{{ record.insuredIdCard || '—' }}</div>
                </template>
                <template slot="detailPolicyStatus" slot-scope="text, record">
                  <a-tag :color="record.policyFileUrl ? 'green' : 'orange'">
                    {{ record.policyFileUrl ? '已上传' : '未上传' }}
                  </a-tag>
                  <div v-if="record.policyUploadTime" class="policy-upload-time">
                    {{ formatDateTime(record.policyUploadTime) }}
                  </div>
                </template>
                <template slot="detailPolicyAction" slot-scope="text, record">
                  <a v-if="record.policyFileUrl" @click="viewPolicy(record)">查看</a>
                  <span v-else>—</span>
                </template>
              </a-table>
            </template>
            <template v-else>
              <a v-if="detailForm.policyFileUrl" @click="viewPolicy(detailForm)">
                {{ detailForm.policyFileName || '查看保单' }}
              </a>
              <span v-else class="insurance-detail-empty">—</span>
            </template>
            <div v-if="detailForm.policyFileUrl && !hasPolicyList(detailForm)" class="detail-policy-action">
              <a-button type="primary" size="small" @click="viewPolicy(detailForm)">查看保单</a-button>
            </div>
          </section>

          <section class="insurance-detail-section">
            <div class="insurance-detail-section__title">处理信息</div>
            <a-descriptions
              bordered
              size="small"
              :column="1"
              class="insurance-detail-desc"
              :label-style="detailDescLabelStyle"
            >
              <a-descriptions-item label="失败原因">{{ detailForm.lastFailReason || '—' }}</a-descriptions-item>
              <a-descriptions-item label="人工备注">{{ detailForm.manualRemark || '—' }}</a-descriptions-item>
            </a-descriptions>
          </section>

          <section class="insurance-detail-section">
            <div class="insurance-detail-section__title">数据快照</div>
            <div class="insurance-detail-snapshot">
              <div class="insurance-detail-snapshot__label">投保人快照</div>
              <a-textarea :value="detailForm.applicantSnapshot || ''" :auto-size="{ minRows: 3, maxRows: 6 }" read-only />
            </div>
            <div class="insurance-detail-snapshot">
              <div class="insurance-detail-snapshot__label">被保人快照</div>
              <a-textarea :value="detailForm.insuredSnapshot || ''" :auto-size="{ minRows: 3, maxRows: 6 }" read-only />
            </div>
          </section>
        </div>
      </a-spin>
    </a-modal>

    <a-modal
      title="回传保单"
      :visible="uploadVisible"
      :confirm-loading="uploadSubmitting"
      @ok="submitUploadPolicy"
      @cancel="closeUploadModal"
    >
      <a-form-model :model="uploadForm" :label-col="{ span: 5 }" :wrapper-col="{ span: 18 }">
        <a-form-model-item label="保险订单">
          <a-input :value="uploadForm.id" disabled />
        </a-form-model-item>
        <a-form-model-item label="被保人" v-if="uploadPolicyList.length > 0">
          <a-select
            v-model="uploadForm.policyId"
            placeholder="请选择被保人"
            @change="handleUploadPolicyChange"
          >
            <a-select-option v-for="item in uploadPolicyList" :key="item.id" :value="item.id">
              {{ formatPolicyOption(item) }}
            </a-select-option>
          </a-select>
        </a-form-model-item>
        <a-form-model-item label="上传 PDF">
          <file-upload
            :key="uploadForm.policyId || uploadForm.id || 'policy-upload'"
            type="file"
            :count="1"
            :defaultList="uploadFileList"
            :max-size="10"
            accept=".pdf,application/pdf"
            @input="handlePolicyFileChange"
          />
          <div class="upload-hint">请上传 PDF 保单，大小不超过 10MB</div>
        </a-form-model-item>
        <a-form-model-item label="处理备注">
          <a-textarea v-model="uploadForm.manualRemark" :auto-size="{ minRows: 3, maxRows: 6 }" />
        </a-form-model-item>
      </a-form-model>
    </a-modal>

    <a-modal
      title="更新投保状态"
      :visible="statusVisible"
      :confirm-loading="statusSubmitting"
      @ok="submitManualStatus"
      @cancel="closeStatusModal"
    >
      <a-form-model :model="statusForm" :label-col="{ span: 5 }" :wrapper-col="{ span: 18 }">
        <a-form-model-item label="保险订单">
          <a-input :value="statusForm.id" disabled />
        </a-form-model-item>
        <a-form-model-item label="投保状态">
          <a-select v-model="statusForm.underwritingStatus" placeholder="请选择投保状态">
            <a-select-option :value="1000">待投保</a-select-option>
            <a-select-option :value="1100">投保中</a-select-option>
            <a-select-option :value="1400">投保失败</a-select-option>
            <a-select-option :value="1500">已取消</a-select-option>
          </a-select>
        </a-form-model-item>
        <a-form-model-item label="失败原因" v-if="statusForm.underwritingStatus === 1400">
          <a-textarea v-model="statusForm.lastFailReason" :auto-size="{ minRows: 3, maxRows: 6 }" />
        </a-form-model-item>
        <a-form-model-item label="处理备注">
          <a-textarea v-model="statusForm.manualRemark" :auto-size="{ minRows: 3, maxRows: 6 }" />
        </a-form-model-item>
      </a-form-model>
    </a-modal>
  </page-header-wrapper>
</template>

<script>
import { parseTime } from '@/utils/ruoyi'
import { tableMixin } from '@/store/table-mixin'
import * as insuranceOrderApi from '@/api/insuranceOrder/insuranceOrder'

const bondPayStatusMap = {
  1: '待支付',
  2: '已支付',
  3: '已取消'
}

const bondPayStatusColorMap = {
  1: 'orange',
  2: 'green',
  3: 'red'
}

const sourceMap = {
  0: '未知',
  10: '雇主订单',
  20: '师傅端购买',
  30: '企业购买'
}

const underwritingStatusMap = {
  0: '未触发投保',
  1000: '待投保',
  1100: '投保中',
  1200: '投保成功',
  1300: '投保失败待处理',
  1400: '投保失败',
  1500: '已取消'
}

const underwritingStatusColorMap = {
  0: 'blue',
  1000: 'cyan',
  1100: 'processing',
  1200: 'green',
  1300: 'orange',
  1400: 'red',
  1500: 'default'
}

export default {
  name: 'InsuranceOrder',
  mixins: [tableMixin],
  data() {
    return {
      bondPayStatusMap,
      bondPayStatusColorMap,
      sourceMap,
      underwritingStatusMap,
      underwritingStatusColorMap,
      underwritingStatusOptions: Object.keys(underwritingStatusMap).map(key => ({
        value: Number(key),
        label: underwritingStatusMap[key]
      })),
      loading: false,
      tableData: [],
      total: 0,
      detailVisible: false,
      detailLoading: false,
      detailForm: {},
      uploadVisible: false,
      uploadSubmitting: false,
      uploadForm: {
        id: null,
        policyId: null,
        policyFileUrl: '',
        policyFileName: '',
        manualRemark: ''
      },
      uploadFileList: [],
      uploadPolicyList: [],
      statusVisible: false,
      statusSubmitting: false,
      statusForm: {
        id: null,
        underwritingStatus: 1100,
        lastFailReason: '',
        manualRemark: ''
      },
      queryParams: {
        pageNum: 1,
        pageSize: 10,
        name: '',
        customerOrderId: undefined,
        bondPayStatus: undefined,
        underwritingStatus: undefined
      },
      columns: [
        { title: '保险订单ID', dataIndex: 'id', width: 170, ellipsis: true },
        { title: '订单号', dataIndex: 'orderNo', width: 160, ellipsis: true },
        { title: '保险计划分类', dataIndex: 'planName', width: 120, ellipsis: true },
        { title: '保险分类', dataIndex: 'insuranceTypeName', width: 100, ellipsis: true },
        { title: '保险方案名称', dataIndex: 'insuranceSchemeName', width: 160, ellipsis: true },
        { title: '投保工种', dataIndex: 'categoryName', width: 140, ellipsis: true },
        { title: '投保人姓名', dataIndex: 'applicantName', width: 120, ellipsis: true },
        { title: '投保人身份证号', dataIndex: 'applicantIdCard', width: 180, ellipsis: true },
       { title: '金额', dataIndex: 'money', width: 100, scopedSlots: { customRender: 'money' } },
        { title: '支付状态', dataIndex: 'bondPayStatus', width: 100, scopedSlots: { customRender: 'status' } },
        { title: '投保来源', dataIndex: 'source', width: 110, scopedSlots: { customRender: 'source' } },
        { title: '投保状态', dataIndex: 'underwritingStatus', width: 120, scopedSlots: { customRender: 'underwritingStatus' } },
        { title: '被保人数', dataIndex: 'insuredCount', width: 90, align: 'center' },
        { title: '最晚提交时间', dataIndex: 'submitDeadline', width: 160, scopedSlots: { customRender: 'submitDeadline' } },
        { title: '最新提交时间', dataIndex: 'lastSubmitTime', width: 160, scopedSlots: { customRender: 'lastSubmitTime' } },
        { title: '支付时间', dataIndex: 'bondPayDate', width: 160, scopedSlots: { customRender: 'bondPayDate' } },
        { title: '保单', dataIndex: 'policyFileName', width: 160, scopedSlots: { customRender: 'policyFileName' } },
        { title: '操作', dataIndex: 'action', width: 280, fixed: 'right', scopedSlots: { customRender: 'action' } }
      ],
      detailPolicyColumns: [
        { title: '被保人', dataIndex: 'insuredName', width: 260, scopedSlots: { customRender: 'detailPolicyInsured' } },
        { title: '保单状态', dataIndex: 'policyFileUrl', width: 140, scopedSlots: { customRender: 'detailPolicyStatus' } },
        { title: '操作', dataIndex: 'action', width: 100, scopedSlots: { customRender: 'detailPolicyAction' } }
      ],
      detailDescLabelStyle: {
        width: '112px',
        minWidth: '112px',
        whiteSpace: 'normal',
        wordBreak: 'keep-all',
        verticalAlign: 'top',
        lineHeight: '1.5'
      }
    }
  },
  computed: {
    detailInsuredPersonList() {
      const form = this.detailForm || {}
      const fromSnap = this.parseInsuredSnapshotArray(form.insuredSnapshot)
      if (fromSnap.length > 0) {
        return fromSnap.map((item, index) => ({
          name: item.name || item.pname || '—',
          idCard: item.idCard || item.cardNumber || item.cardno || '—',
          sex: item.sex,
          tableKey: `${item.id != null ? item.id : index}-${item.idCard || item.cardNumber || item.name || ''}`
        }))
      }
      const splitParts = (s) =>
        String(s || '')
          .split(/\r?\n|、|;/)
          .map((t) => t.trim())
          .filter((t) => t)
      const names = splitParts(form.insuredNames)
      const ids = splitParts(form.insuredIdCards)
      const n = Math.max(names.length, ids.length)
      if (n === 0) {
        return []
      }
      const rows = []
      for (let i = 0; i < n; i++) {
        rows.push({
          name: names[i] || '—',
          idCard: ids[i] || '—',
          sex: undefined,
          tableKey: `fallback-${i}-${ids[i] || names[i] || ''}`
        })
      }
      return rows
    }
  },
  created() {
    this.getList()
  },
  methods: {
    parseTime,
    formatDateTime(value) {
      return value ? parseTime(value, '{y}-{m}-{d} {h}:{i}:{s}') : '—'
    },
    formatDetailInsuredSex(sex) {
      if (sex === '男' || sex === '女') return sex
      if (Number(sex) === 2) return '女'
      if (sex != null && sex !== '') return '男'
      return '—'
    },
    parseInsuredSnapshotArray(value) {
      if (!value || typeof value !== 'string') return []
      try {
        const parsed = JSON.parse(value)
        return Array.isArray(parsed) ? parsed : []
      } catch (e) {
        return []
      }
    },
    getList() {
      this.loading = true
      insuranceOrderApi.pageInsurance(this.queryParams)
        .then(res => {
          if (res && res.success) {
            this.tableData = (res.data && res.data.records) || []
            this.total = (res.data && res.data.total) || 0
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    handleQuery() {
      this.queryParams.pageNum = 1
      this.getList()
    },
    resetQuery() {
      this.queryParams = {
        pageNum: 1,
        pageSize: this.queryParams.pageSize,
        name: '',
        customerOrderId: undefined,
        bondPayStatus: undefined,
        underwritingStatus: undefined
      }
      this.getList()
    },
    onShowSizeChange(current, pageSize) {
      this.queryParams.pageSize = pageSize
      this.queryParams.pageNum = 1
      this.getList()
    },
    changeSize(current, pageSize) {
      this.queryParams.pageNum = current
      this.queryParams.pageSize = pageSize
      this.getList()
    },
    handleExport() {
      this.download(
        'insuranceOrder/insuranceOrder/export',
        { ...this.queryParams },
        `保险订单_${new Date().getTime()}.xls`
      )
    },
    canProcess(record) {
      return record && record.bondPayStatus === 2
    },
    hasPolicyList(record) {
      return Array.isArray(record && record.policyList) && record.policyList.length > 0
    },
    policyListRows(record) {
      return this.hasPolicyList(record) ? record.policyList : []
    },
    policyProgressText(record) {
      const total = this.policyTotalCount(record)
      const uploaded = this.policyUploadedCount(record)
      return total > 0 ? `已上传 ${uploaded}/${total}` : '—'
    },
    policyTotalCount(record) {
      if (record && record.policyTotalCount != null) {
        return Number(record.policyTotalCount) || 0
      }
      return this.hasPolicyList(record) ? record.policyList.length : 0
    },
    policyUploadedCount(record) {
      if (record && record.policyUploadedCount != null) {
        return Number(record.policyUploadedCount) || 0
      }
      if (!this.hasPolicyList(record)) {
        return record && record.policyFileUrl ? 1 : 0
      }
      return record.policyList.filter(item => item && item.policyFileUrl).length
    },
    openDetail(record) {
      this.detailVisible = true
      this.detailLoading = true
      insuranceOrderApi.getUnderwritingDetail({ id: record.id })
        .then(res => {
          if (res && res.success) {
            this.detailForm = res.data || {}
          } else {
            this.detailForm = {}
          }
        })
        .finally(() => {
          this.detailLoading = false
        })
    },
    closeDetail() {
      this.detailVisible = false
      this.detailForm = {}
    },
    openUploadModal(record) {
      this.uploadVisible = true
      this.uploadPolicyList = Array.isArray(record.policyList) ? record.policyList.slice() : []
      const firstPolicy = this.uploadPolicyList[0] || null
      const multiInsured = this.uploadPolicyList.length > 1
      this.uploadForm = {
        id: record.id,
        policyId: multiInsured ? null : (firstPolicy ? firstPolicy.id : null),
        policyFileUrl: multiInsured ? '' : (firstPolicy ? (firstPolicy.policyFileUrl || '') : (record.policyFileUrl || '')),
        policyFileName: multiInsured ? '' : (firstPolicy ? (firstPolicy.policyFileName || '') : (record.policyFileName || '')),
        manualRemark: record.manualRemark || ''
      }
      this.uploadFileList = this.buildFileList(this.uploadForm.policyFileUrl, this.uploadForm.policyFileName)
    },
    closeUploadModal() {
      this.uploadVisible = false
      this.uploadSubmitting = false
      this.uploadForm = {
        id: null,
        policyId: null,
        policyFileUrl: '',
        policyFileName: '',
        manualRemark: ''
      }
      this.uploadFileList = []
      this.uploadPolicyList = []
    },
    formatPolicyOption(item) {
      const name = item && item.insuredName ? item.insuredName : '未命名被保人'
      const card = item && item.insuredIdCard ? item.insuredIdCard : '无身份证'
      const status = item && item.policyFileUrl ? '已上传' : '未上传'
      return `${name} / ${card} / ${status}`
    },
    handleUploadPolicyChange(policyId) {
      const policy = this.uploadPolicyList.find(item => item.id === policyId)
      this.uploadForm.policyFileUrl = policy ? (policy.policyFileUrl || '') : ''
      this.uploadForm.policyFileName = policy ? (policy.policyFileName || '') : ''
      this.uploadFileList = this.buildFileList(this.uploadForm.policyFileUrl, this.uploadForm.policyFileName)
    },
    handlePolicyFileChange(fileList) {
      if (!Array.isArray(fileList) || fileList.length === 0) {
        this.uploadForm.policyFileUrl = ''
        this.uploadForm.policyFileName = ''
        this.uploadFileList = []
        return
      }
      const firstFile = fileList[0]
      const fileUrl = firstFile.url || firstFile.path || firstFile.halfUrl || ''
      const fileName = firstFile.name || this.extractFileName(fileUrl)
      if (!this.isPdfFile(fileName) && !this.isPdfFile(fileUrl)) {
        this.$message.warning('请上传 PDF 格式的保单文件')
        this.uploadForm.policyFileUrl = ''
        this.uploadForm.policyFileName = ''
        this.uploadFileList = []
        return
      }
      this.uploadForm.policyFileUrl = fileUrl
      this.uploadForm.policyFileName = fileName
      this.uploadFileList = fileList
    },
    submitUploadPolicy() {
      if (this.uploadPolicyList.length > 0 && !this.uploadForm.policyId) {
        this.$message.warning('多人订单请先选择被保人后再上传保单')
        return
      }
      if (this.uploadPolicyList.length === 0 && !this.uploadForm.id) {
        this.$message.warning('保险订单数据异常')
        return
      }
      if (!this.uploadForm.policyFileUrl) {
        this.$message.warning('请先上传保单 PDF')
        return
      }
      if (!this.isPdfFile(this.uploadForm.policyFileName) && !this.isPdfFile(this.uploadForm.policyFileUrl)) {
        this.$message.warning('请上传 PDF 格式的保单文件')
        return
      }
      const currentId = this.uploadForm.id
      this.uploadSubmitting = true
      insuranceOrderApi.uploadInsurancePolicy(this.uploadForm)
        .then(() => {
          this.$message.success('保单回传成功')
          this.closeUploadModal()
          this.getList()
          if (this.detailVisible && this.detailForm.id === currentId) {
            this.openDetail({ id: currentId })
          }
        })
        .finally(() => {
          this.uploadSubmitting = false
        })
    },
    openStatusModal(record) {
      this.statusVisible = true
      this.statusForm = {
        id: record.id,
        underwritingStatus: record.underwritingStatus === 1200 ? 1100 : (record.underwritingStatus || 1100),
        lastFailReason: record.lastFailReason || '',
        manualRemark: record.manualRemark || ''
      }
    },
    closeStatusModal() {
      this.statusVisible = false
      this.statusSubmitting = false
      this.statusForm = {
        id: null,
        underwritingStatus: 1100,
        lastFailReason: '',
        manualRemark: ''
      }
    },
    submitManualStatus() {
      if (!this.statusForm.underwritingStatus) {
        this.$message.warning('请选择投保状态')
        return
      }
      if (this.statusForm.underwritingStatus === 1400 && !this.statusForm.lastFailReason) {
        this.$message.warning('投保失败时请填写失败原因')
        return
      }
      const currentId = this.statusForm.id
      this.statusSubmitting = true
      insuranceOrderApi.updateInsuranceManualStatus(this.statusForm)
        .then(() => {
          this.$message.success('投保状态更新成功')
          this.closeStatusModal()
          this.getList()
          if (this.detailVisible && this.detailForm.id === currentId) {
            this.openDetail({ id: currentId })
          }
        })
        .finally(() => {
          this.statusSubmitting = false
        })
    },
    viewPolicy(record) {
      const url = record && record.policyFileUrl
      if (!url) {
        this.$message.warning('暂无保单文件')
        return
      }
      window.open(url, '_blank')
    },
    buildFileList(url, name) {
      if (!url) {
        return []
      }
      return [{
        uid: String(Date.now()),
        name: name || this.extractFileName(url),
        status: 'done',
        url,
        path: url
      }]
    },
    extractFileName(url) {
      if (!url) {
        return ''
      }
      const cleanUrl = String(url).split('?')[0].split('#')[0]
      const segments = cleanUrl.split('/')
      return segments[segments.length - 1] || ''
    },
    isPdfFile(value) {
      const fileName = this.extractFileName(value)
      return !!fileName && fileName.toLowerCase().endsWith('.pdf')
    }
  }
}
</script>

<style lang="less" scoped>
.table-page-search-wrapper {
  margin-bottom: 16px;
}

.table-operations {
  margin-bottom: 16px;
}

.ant-table-pagination {
  margin-top: 16px;
  text-align: right;
}

.upload-hint {
  margin-top: 8px;
  color: #999;
  font-size: 12px;
}

.insurance-detail-body {
  .insurance-detail-section + .insurance-detail-section {
    margin-top: 20px;
  }
}

.insurance-detail-section__title {
  margin-bottom: 10px;
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
}

.insurance-detail-desc /deep/ .ant-descriptions-view table {
  table-layout: fixed;
  width: 100%;
}

.insurance-detail-desc /deep/ .ant-descriptions-item-label {
  white-space: normal !important;
  word-break: keep-all;
  writing-mode: horizontal-tb;
}

.insurance-detail-desc /deep/ .ant-descriptions-item-content {
  word-break: break-all;
  vertical-align: top;
}

.insurance-detail-empty {
  color: rgba(0, 0, 0, 0.25);
}

.insurance-detail-snapshot + .insurance-detail-snapshot {
  margin-top: 12px;
}

.insurance-detail-snapshot__label {
  margin-bottom: 6px;
  color: rgba(0, 0, 0, 0.45);
  font-size: 13px;
}

.policy-summary {
  margin-bottom: 4px;
  color: rgba(0, 0, 0, 0.65);
}

.detail-policy-action {
  margin-top: 12px;
}

.policy-table {
  margin-top: 8px;
}

.policy-card-no,
.policy-upload-time {
  margin-top: 4px;
  color: #999;
  font-size: 12px;
}

.detail-insured-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detail-insured-item {
  padding: 10px 12px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
}

.detail-insured-item__head {
  margin-bottom: 8px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
}

.detail-insured-item__row {
  margin-top: 4px;
  line-height: 1.6;
  word-break: break-all;
}

.detail-insured-label {
  display: inline-block;
  min-width: 52px;
  margin-right: 8px;
  color: rgba(0, 0, 0, 0.45);
  font-size: 12px;
}

.detail-insured-value {
  font-size: 13px;
}
</style>
